import { r as createLogger } from "../logger-BU8YqMGU.js";
import { endSession, recordWriteFailure, startSession } from "./recording-slice.js";
//#region ../src/state/persistence-middleware.ts
const log = createLogger("PersistenceMiddleware");
/** Maximum number of concurrent OPFS write operations. */
const MAX_CONCURRENT_WRITES = 3;
/**
* Simple write queue that limits concurrent OPFS operations.
* Prevents unbounded memory growth when storage is slow.
*/
var WriteQueue = class {
	pendingCount = 0;
	queue = [];
	flushWaiters = [];
	enqueue(writeFn) {
		this.queue.push(writeFn);
		this.drain();
	}
	/**
	* Resolves once the queue is fully idle — every enqueued write (including
	* ones still waiting behind the concurrency cap) has settled. Rejected
	* writes count as settled (the enqueue wrappers handle their errors), so
	* flush never hangs on a failing backend. Writes enqueued AFTER the queue
	* went idle again are not waited for.
	*/
	flush() {
		if (this.isIdle()) return Promise.resolve();
		return new Promise((resolve) => {
			this.flushWaiters.push(resolve);
		});
	}
	isIdle() {
		return this.pendingCount === 0 && this.queue.length === 0;
	}
	drain() {
		while (this.pendingCount < MAX_CONCURRENT_WRITES && this.queue.length > 0) {
			const fn = this.queue.shift();
			this.pendingCount++;
			fn().finally(() => {
				this.pendingCount--;
				this.drain();
			});
		}
		if (this.isIdle() && this.flushWaiters.length > 0) {
			const waiters = this.flushWaiters;
			this.flushWaiters = [];
			for (const resolve of waiters) resolve();
		}
	}
};
/**
* Extract the slice prefix from a namespaced Redux action type.
*
* `'gpsData/setZeroPos'` → `'gpsData'`. Returns the whole string when there
* is no slash (e.g. `'@@INIT'`). This is the single point that turns a
* slice-owned action type into the value the persistence whitelist matches
* on, so call sites can derive prefixes from real action creators instead of
* re-typing literals.
*/
function slicePrefixOf(actionType) {
	const slashIndex = actionType.indexOf("/");
	return slashIndex === -1 ? actionType : actionType.slice(0, slashIndex);
}
/** Read `state.recording.isRecording`, defaulting to false for any shape. */
function readIsRecording(state) {
	return state?.recording?.isRecording ?? false;
}
/**
* Decide whether the just-reduced action belongs to a session whose actions
* must be persisted. True while recording, and also for the `endSession`
* action that just flipped `isRecording` to false (Issue 5) so the session's
* final action is still captured.
*
* The endSession type is derived from the imported action creator
* (`endSession.type`) rather than a hand-typed literal, so a rename of the
* recording slice or its actions propagates here automatically instead of
* silently breaking final-action persistence.
*/
function isInPersistableSession(wasRecording, isRecording, actionType) {
	const isEndSession = actionType === endSession.type;
	return isRecording || wasRecording && isEndSession;
}
function createPersistenceMiddleware(options) {
	const { storageBackend, onWriteFailure, persistedPrefixes } = options;
	const normalizedPrefixes = persistedPrefixes.map((prefix) => prefix.endsWith("/") ? prefix : `${prefix}/`);
	const excludedActionType = recordWriteFailure.type;
	let actionIndex = 0;
	const writeQueue = new WriteQueue();
	let dispatchDepth = 0;
	let hasWarnedReentrant = false;
	const middleware = (store) => (next) => (action) => {
		dispatchDepth += 1;
		try {
			const typedAction = action;
			const actionType = typeof typedAction.type === "string" ? typedAction.type : null;
			const wasRecording = readIsRecording(store.getState());
			const result = next(action);
			if (!actionType) return result;
			if (actionType === startSession.type) actionIndex = 0;
			if (!isInPersistableSession(wasRecording, readIsRecording(store.getState()), actionType)) return result;
			if (!(actionType !== excludedActionType && normalizedPrefixes.some((prefix) => actionType.startsWith(prefix)))) return result;
			const index = ++actionIndex;
			if (dispatchDepth > 1 && !hasWarnedReentrant) {
				hasWarnedReentrant = true;
				log.warn(`Persisted action "${actionType}" was dispatched re-entrantly (inside another dispatch's next()). Its recorded order can invert on replay — react to actions via a prepended listener middleware, not a synchronous store.subscribe dispatch. See 2026-06-28-0751-subscriber-dispatch-persistence-ordering-review.md.`);
			}
			writeQueue.enqueue(async () => {
				try {
					await storageBackend.writeAction(typedAction, index);
				} catch (err) {
					const normalized = err instanceof Error ? err : new Error(String(err));
					log.error("Failed to persist action:", normalized);
					store.dispatch(recordWriteFailure(normalized.message));
					if (onWriteFailure) onWriteFailure(normalized);
				}
			});
			return result;
		} finally {
			dispatchDepth -= 1;
		}
	};
	return Object.assign(middleware, { flushPendingWrites: () => writeQueue.flush() });
}
//#endregion
export { createPersistenceMiddleware, slicePrefixOf };
